/**
 * trace-dialog.tsx — Hộp thoại "số này ở đâu ra".
 *
 * Mở khi bấm vào bất kỳ con số nào trên màn Tổng quan. Ba phần, theo đúng thứ
 * tự câu hỏi người xem đặt ra:
 *
 *  1. Con số là bao nhiêu, của kỳ nào.
 *  2. Lấy từ bảng nào, lọc những điều kiện gì (và công thức, nếu là tỉ lệ).
 *  3. Chính những dòng dữ liệu đã cộng thành con số đó.
 *
 * Backend dùng CHUNG một mệnh đề WHERE cho cả phép đếm lẫn phép lấy dòng, nên
 * tổng ở phần 1 và bảng ở phần 3 không thể lệch nhau.
 *
 * Điện thoại (< 768px): hộp trượt từ đáy, phần 3 là DANH SÁCH THẺ (mỗi dòng dữ liệu một thẻ) thay cho bảng cuộn ngang.
 */
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Database, Sigma } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useDrilldown, type ChiSoTruyVet } from '@/hooks/use-drilldown'
import type { ChatReportMode, ChatReportScope } from '@/hooks/use-chat-report'
import { cn, formatNumber } from '@/lib/utils'
import { dinhDangTong, oO, tachTieuDe, truongCuaThe } from './trace-format'

export function TraceDialog({
  metric, mode, scope, onClose,
}: {
  metric: ChiSoTruyVet | null
  mode: ChatReportMode
  scope?: ChatReportScope
  onClose: () => void
}) {
  const { t } = useTranslation('reports')
  const isMobile = useIsMobile()
  const [trang, setTrang] = useState(1)
  // Đổi chỉ số thì về trang 1 — giữ trang cũ của chỉ số khác là vô nghĩa.
  useEffect(() => { setTrang(1) }, [metric])

  const { data, isLoading, isError } = useDrilldown(metric, mode, scope, trang)

  const soTrang = data ? Math.max(1, Math.ceil(data.soDong / data.soDongMoiTrang)) : 1
  const dauTrang = data ? (data.trang - 1) * data.soDongMoiTrang : 0

  const subtitle = data
    ? data.theoKy
      ? t('traceDialog.period', { period: data.kyNhan })
      : t('traceDialog.snapshot')
    : t('traceDialog.loading')
  const title = data?.tieuDe ?? t('traceDialog.defaultTitle')

  const body = (
    <>
        {isError ? (
          <ErrorState message={t('traceDialog.error')} />
        ) : isLoading || !data ? (
          <Loading />
        ) : (
          <div className="space-y-4">
            {/* ① Con số */}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border bg-muted/40 px-4 py-3">
              <span className="text-3xl font-bold tabular-nums">
                {dinhDangTong(data.metric, data.tong)}
              </span>
              <span className="text-xs text-muted-foreground">
                {data.congThuc
                  // Tỉ lệ: con số là thương, bảng bên dưới là các dòng của TỬ SỐ.
                  ? t('traceDialog.calcRatio', { count: formatNumber(data.soDong) })
                  : data.cachTinhTong === 'cong'
                    ? t('traceDialog.calcSum', { count: formatNumber(data.soDong) })
                    : t('traceDialog.calcExact', { count: formatNumber(data.soDong) })}
              </span>
            </div>

            {/* ② Nguồn + công thức */}
            <div className="space-y-2 text-xs">
              <p className="flex items-center gap-1.5 font-semibold">
                <Database className="h-3.5 w-3.5 text-muted-foreground" />
                {t('traceDialog.dataSource')}
              </p>
              <p className="font-mono text-[11px] text-muted-foreground">{data.nguon.bang}</p>
              <ul className="ml-4 list-disc space-y-1 text-muted-foreground">
                {data.nguon.dieuKien.map((d) => <li key={d}>{d}</li>)}
              </ul>

              {data.congThuc && (
                <div className="rounded-md border border-dashed p-3">
                  <p className="flex items-center gap-1.5 font-semibold">
                    <Sigma className="h-3.5 w-3.5 text-muted-foreground" />
                    {t('traceDialog.formula', { expr: data.congThuc.bieuThuc })}
                  </p>
                  <div className="mt-1.5 space-y-0.5 text-muted-foreground">
                    {data.congThuc.thanhPhan.map((tp) => (
                      <p key={tp.nhan}>
                        {tp.nhan}: <span className="font-semibold text-foreground">{formatNumber(Number(tp.giaTri))}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {data.ghiChu && (
                <p className="rounded-md bg-warning/10 px-3 py-2 text-warning">{data.ghiChu}</p>
              )}
            </div>

            {/* ③ Dữ liệu chi tiết */}
            {data.dong.length === 0 ? (
              <p className="rounded-md border border-dashed py-8 text-center text-xs text-muted-foreground">
                {t('traceDialog.noRows')}
              </p>
            ) : isMobile ? (
              <ul className="space-y-2.5" aria-label={t('traceDialog.rowsAria')}>
                {data.dong.map((r, i) => {
                  const { tieuDe, than } = tachTieuDe(truongCuaThe(data.cot, r))
                  return (
                    <li key={i} className="rounded-xl border bg-card p-3.5">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold tabular-nums text-muted-foreground">
                          {dauTrang + i + 1}
                        </span>
                        {tieuDe && <p className="min-w-0 flex-1 break-words text-[15px] font-semibold leading-snug">{tieuDe.value}</p>}
                      </div>
                      {than.length > 0 && (
                        <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
                          {than.map((f) => (
                            <div key={f.key} className={cn('min-w-0', f.rong && 'col-span-2')}>
                              <dt className="text-[11px] text-muted-foreground">{f.nhan}</dt>
                              <dd className={cn('break-words text-sm leading-snug', f.so && 'font-medium tabular-nums')}>{f.value}</dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/70 text-left">
                    <tr>
                      <th className="w-10 px-2 py-2 font-semibold text-muted-foreground">#</th>
                      {data.cot.map((c) => (
                        <th key={c.key} className="whitespace-nowrap px-3 py-2 font-semibold">{c.nhan}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.dong.map((r, i) => (
                      <tr key={i} className="border-t align-top hover:bg-accent/40">
                        <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{dauTrang + i + 1}</td>
                        {data.cot.map((c) => (
                          <td
                            key={c.key}
                            className={cn(
                              'px-3 py-1.5',
                              c.kieu === 'so' || c.kieu === 'tien' ? 'tabular-nums' : '',
                              c.key === 'noi_dung' ? 'min-w-[16rem] max-w-md' : 'whitespace-nowrap',
                            )}
                          >
                            {oO(r[c.key], c)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {soTrang > 1 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {t('traceDialog.rowsRange', {
                    from: formatNumber(dauTrang + 1),
                    to: formatNumber(dauTrang + data.dong.length),
                    total: formatNumber(data.soDong),
                  })}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={trang <= 1} onClick={() => setTrang((p) => p - 1)}>
                    <ChevronLeft className="h-3.5 w-3.5" />
                    {t('traceDialog.prev')}
                  </Button>
                  <span className="tabular-nums">{trang}/{formatNumber(soTrang)}</span>
                  <Button
                    variant="outline" size="sm"
                    disabled={trang >= soTrang}
                    onClick={() => setTrang((p) => p + 1)}
                  >
                    {t('traceDialog.next')}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
    </>
  )

  if (isMobile) {
    return (
      <BottomSheet open={!!metric} onOpenChange={(o) => { if (!o) onClose() }}>
        <BottomSheetContent title={title} className="h-[92dvh]">
          <div className="space-y-4 px-4 pb-6 pt-2">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold leading-snug">{title}</h2>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
            {body}
          </div>
        </BottomSheetContent>
      </BottomSheet>
    )
  }

  return (
    <Dialog open={!!metric} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="pr-8">{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        {body}
      </DialogContent>
    </Dialog>
  )
}
